import React, { useState, useEffect, useRef } from "react"
import { Container, Row, Col, Card, CardBody, Button, Form, FormGroup, Label, Alert, Spinner, Progress, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap"
import Select from "react-select"
import { useRunTestMutation, useGetTestByIdQuery, useGetTestResultsQuery, useStartTestMutation, usePauseTestMutation, useCancelTestMutation, useDeleteTestMutation } from "../../store/tests/tests.api"
import { useGetCustomersQuery, useLazyGetTestNodeContactsQuery } from "../../store/customer/customer.api"
import { useGetOperatorsQuery } from "../../store/operator/operator.api"
import { useGetTemplatesQuery, useLazyGetTemplateLanguagesQuery, useLazyGetTemplateMessagesQuery } from "../../store/template/template.api"
import { useGetPartnersQuery } from "../../store/partner/partner.api"
import { useHistory, useLocation } from "react-router-dom"
import testSvg from "../../assets/images/test.svg"
import _ from "lodash"
import io from "socket.io-client"
import * as urls from "../../helpers/url_helper"

// Debug logging helper
const log = (action, data) => {
  console.log(`[FE-DEBUG-NewTest.js] ${new Date().toISOString()} - ${action}`, data || '');
};

// Remove hardcoded customer options
// const customerOptions = [
//   { value: "Axiata", label: "Axiata" },
//   { value: "Acme", label: "Acme Inc." },
// ]

// Remove hardcoded operator options
// const operatorOptions = [
//   { value: "Dialog-Sri Lanka", label: "Dialog-Sri Lanka" },
//   { value: "Vodafone", label: "Vodafone" },
// ]

// Remove hardcoded phone number options as we'll fetch them from the API
// const phoneNumberOptions = [
//   { value: "+94123123123", label: "+94123123123" },
//   { value: "+94123123124", label: "+94123123124" },
//   { value: "+94123456789", label: "+94123456789" },
//   { value: "+94987654321", label: "+94987654321" },
// ]

// Remove hardcoded template options as we'll fetch them from the API
// Remove hardcoded language options as we'll fetch them from the API
// const languageOptions = [
//   { value: "English", label: "English" },
//   { value: "Spanish", label: "Spanish" },
// ]

// Remove hardcoded route options as we'll fetch them from partners API
// const availableRoutes = [
//   { value: "Active LM", label: "Active LM" },
//   { value: "Active IB", label: "Active IB" },
//   { value: "Fallback", label: "Fallback" },
//   { value: "Secondary", label: "Secondary" },
// ]

// Remove hardcoded message text options as we'll fetch them from the API
// const messageTextOptions = [
//   { value: "00000000 is your verification code for the Sony account.", label: "00000000 is your verification code for the Sony account.", format: "Numeric", type: "Mixed", codeLength: "6" },
//   { value: "12345678 is your OTP for login.", label: "12345678 is your OTP for login.", format: "Numeric", type: "Numeric", codeLength: "8" },
//   { value: "Your code is 87654321.", label: "Your code is 87654321.", format: "Alphanumeric", type: "Alpha", codeLength: "8" },
// ]

const customSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "38px",
    height: "38px",
    borderColor: state.isFocused ? "#2196f3" : "#ddd",
    boxShadow: "none",
    fontSize: "14px",
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    minHeight: "38px",
    maxHeight: "38px",
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    overflowY: "auto",
  }),
  multiValue: (base) => ({
    ...base,
    background: "#811113",
    color: "white",
    borderRadius: "12px",
    minHeight: "22px",
    height: "22px",
    padding: "0 4px",
    alignItems: "center",
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: "white",
    fontSize: "12px",
    padding: "0 4px",
    lineHeight: 1.2,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: "white",
    ':hover': {
      backgroundColor: '#a81a1a',
      color: 'white',
    },
    fontSize: "14px",
    marginLeft: "2px"
  }),
  placeholder: (base) => ({
    ...base,
    color: "#888",
    fontSize: "14px",
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: "4px",
  }),
}

const NewTest = () => {
  const history = useHistory()
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const testId = query.get('id')
  const viewMode = query.get('view') === 'true'
  
  // Initialize state for socket and real-time data
  const [socket, setSocket] = useState(null)
  const [realTimeProgress, setRealTimeProgress] = useState(null)
  const [realTimeStatus, setRealTimeStatus] = useState(null)
  const [formDisabled, setFormDisabled] = useState(viewMode)
  const [showResults, setShowResults] = useState(viewMode)
  const [progressDetails, setProgressDetails] = useState(null)
  
  // Add state for real-time results
  const [realTimeResults, setRealTimeResults] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  const [pendingRetries, setPendingRetries] = useState({})
  
  const [createdTestId, setCreatedTestId] = useState(null)
  
  // Add pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [resultsPerPage] = useState(10)
  
  // Get test data if in view mode
  const { 
    data: testData, 
    isLoading: testLoading, 
    error: testError 
  } = useGetTestByIdQuery(testId, { skip: !testId })
  
  // Get test results if in view mode
  const { 
    data: resultsData, 
    isLoading: resultsLoading, 
    error: resultsError 
  } = useGetTestResultsQuery(testId, { skip: !testId })
  
  // Start, pause, stop, and delete test mutations
  const [startTest, { isLoading: starting }] = useStartTestMutation()
  const [pauseTest, { isLoading: pausing }] = usePauseTestMutation()
  const [cancelTest, { isLoading: cancelling }] = useCancelTestMutation()
  const [deleteTest, { isLoading: deleting }] = useDeleteTestMutation()
  
  // Regular form state
  const [testConfig, setTestConfig] = useState({
    testName: "Unnamed Test",
    customer: null,
    operator: null,
    phoneNumbers: [],
    template: null,
    language: null,
    messageText: null,
    format: null,
    type: null,
    codeLength: null,
    routes: [],
    customSupplierId: null,  // Add custom supplier ID field
    delayTime: 2.5  // Add delay time field with default 2.5 seconds
  })
  
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [selectedOperator, setSelectedOperator] = useState(null)
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState(null)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [selectedLanguage, setSelectedLanguage] = useState(null)
  // Fetch customers data
  const { data: customersData, isLoading: isLoadingCustomers, error: customersError } = useGetCustomersQuery()
  
  // Fetch operators data
  const { data: operatorsData, isLoading: isLoadingOperators, error: operatorsError } = useGetOperatorsQuery({ customer_body: {
    customer_id: selectedCustomer
  } },{
    refetchOnMountOrArgChange: true
  })
  
  // Fetch templates data
  const { data: templatesData, isLoading: isLoadingTemplates, error: templatesError } = useGetTemplatesQuery()
  
  // Fetch partners data for routes
  const { data: partnersData, isLoading: isLoadingPartners, error: partnersError } = useGetPartnersQuery({ active: true })
  
  // Fetch phone numbers when both customer and operator are selected
  const [getPhoneNumbers, { 
    data: phoneNumbersData, 
    isLoading: isLoadingPhoneNumbers, 
    error: phoneNumbersError 
  }] = useLazyGetTestNodeContactsQuery()
  
  // Add a trigger to force refetching phone numbers
  const [phoneNumbersFetchTrigger, setPhoneNumbersFetchTrigger] = useState(0)
  
  // Add triggers for template languages and message texts
  const [languagesFetchTrigger, setLanguagesFetchTrigger] = useState(0)
  const [messagesFetchTrigger, setMessagesFetchTrigger] = useState(0)
  
  const [customerOptions, setCustomerOptions] = useState([])
  const [operatorOptions, setOperatorOptions] = useState([])
  const [phoneNumberOptions, setPhoneNumberOptions] = useState([])
  const [templateOptions, setTemplateOptions] = useState([])
  const [languageOptions, setLanguageOptions] = useState([])
  const [messageTextOptions, setMessageTextOptions] = useState([])
  const [routeOptions, setRouteOptions] = useState([])
  
  // Lazy query to get languages for a template
  const [getTemplateLanguages, { 
    data: languagesData, 
    isLoading: isLoadingLanguages, 
    error: languagesError 
  }] = useLazyGetTemplateLanguagesQuery()
  
  // Lazy query to get message templates for a template
  const [getTemplateMessages, { 
    data: messagesData, 
    isLoading: isLoadingMessages, 
    error: messagesError 
  }] = useLazyGetTemplateMessagesQuery()
  
  // Format customers data for dropdown
  useEffect(() => {
    if (customersData) {
      let data = _.get(customersData, "data", [])
      setCustomerOptions(data.map(customer => ({
        value: customer.id.toString(),
        label: customer.name
      })))
    }
  }, [customersData])
  
  // Format operators data for dropdown
  useEffect(() => {
    if (operatorsData) {
      let data = _.get(operatorsData, "message", [])
      setOperatorOptions(data.map(operator => ({
        value: operator.id.toString(),
        label: operator.operator_name+"-"+operator.country
      })))
    }
  }, [operatorsData])
  
  // Format template data for dropdown
  useEffect(() => {
    if (templatesData) {
      // Adjust the data structure path according to your API response
      const templates = _.get(templatesData, "templates", []) || _.get(templatesData, "message", []) || []
      // const templates = templatesData || []
      setTemplateOptions(templates.map(template => ({
        value: template.id.toString(),
        label: template.label || template.template_name,
        sender: template.sender  // Include sender from DB for accurate sender resolution
      })))
    }
  }, [templatesData])
  
  // Fetch phone numbers when both customer and operator are selected
  useEffect(() => {
    if (selectedCustomer && selectedOperator) {
      getPhoneNumbers({
        customer_id: selectedCustomer,
        operator_id: selectedOperator
      })
    } else {
      // Reset phone numbers if customer or operator is cleared
      setPhoneNumberOptions([])
      setTestConfig(prev => ({
        ...prev,
        phoneNumbers: []
      }))
    }
  }, [selectedCustomer, selectedOperator, phoneNumbersFetchTrigger, getPhoneNumbers])
  
  // Format phone numbers data for dropdown
  useEffect(() => {
    // Only update phone number options if the data corresponds to the current selections
    if (phoneNumbersData && phoneNumbersData.contacts) {
      setPhoneNumberOptions(phoneNumbersData.contacts.map(contact => ({
        value: contact.phone,
        label: contact.comments && contact.comments.trim() !== '' ? `${contact.phone} (${contact.comments})` : contact.phone
      })))
    } else if (!selectedCustomer || !selectedOperator) {
      // Ensure phone numbers are cleared when selections are cleared
      setPhoneNumberOptions([])
    }
  }, [phoneNumbersData, selectedCustomer, selectedOperator])
  
  // Fetch languages when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      getTemplateLanguages(selectedTemplate)
    } else {
      // Reset languages if template is cleared
      setLanguageOptions([])
      setTestConfig(prev => ({
        ...prev,
        language: null
      }))
    }
  }, [selectedTemplate, languagesFetchTrigger, getTemplateLanguages])
  
  // Format language data for dropdown
  useEffect(() => {
    if (languagesData && languagesData.languages) {
      setLanguageOptions(languagesData.languages.map(language => ({
        value: language.id.toString(),
        label: language.value
      })))
    } else if (!selectedTemplate) {
      // Ensure languages are cleared when template is not selected
      setLanguageOptions([])
    }
  }, [languagesData, selectedTemplate])
  
  // Fetch message templates when both template and language are selected
  useEffect(() => {
    if (selectedTemplate && selectedLanguage) {
      getTemplateMessages({
        templateId: selectedTemplate,
        languageId: selectedLanguage
      })
    } else {
      // Reset message templates if template or language is cleared
      setMessageTextOptions([])
      setTestConfig(prev => ({
        ...prev,
        messageText: null,
        format: null,
        type: null,
        codeLength: null
      }))
    }
  }, [selectedTemplate, selectedLanguage, messagesFetchTrigger, getTemplateMessages])
  
  // Format message templates data for dropdown
  useEffect(() => {
    if (messagesData && messagesData.messageTemplates) {
      setMessageTextOptions(messagesData.messageTemplates.map(message => ({
        value: message.value,
        label: message.value,
        format: message.format,
        type: message.type,
        codeLength: message.codeLength
      })))
    } else if (!selectedTemplate || !selectedLanguage) {
      // Ensure message texts are cleared when template or language is not selected
      setMessageTextOptions([])
    }
  }, [messagesData, selectedTemplate, selectedLanguage])
  
  // Format partners data for routes dropdown
  useEffect(() => {
    if (partnersData) {
      // The structure of partnersData might vary, adjust the path as needed
      const partners = _.get(partnersData, "partners", []) || _.get(partnersData, "data", []) || []
      
      // Filter out partners without a route type and map the rest to route options
      const routes = partners
        .filter(partner => partner.route_type)
        .map(partner => {
          const routeType = partner.route_type ? ` (${partner.route_type.name || partner.route_type})` : ''
          return {
            value: partner.route_type,
            label: `${partner.partner_name}${routeType}`,
            partnerId: partner.id,
            routeType: partner.route_type
          }
        })
      
      setRouteOptions(routes)
    }
  }, [partnersData])
  
  const [runTest, { isLoading, isSuccess, error, data: runTestData }] = useRunTestMutation()
  const [formError, setFormError] = useState("")

  const handleSelectChange = (name, value) => {
    if (name === "customer") {
      setSelectedCustomer(value?.value || null);
      setSelectedOperator(null);
      setTestConfig(prev => ({
        ...prev,
        customer: value,
        operator: null,
        phoneNumbers: [],
      }));
      return;
    }
    if (name === "operator") {
      setSelectedOperator(value?.value || null);
      setTestConfig(prev => ({
        ...prev,
        operator: value,
        phoneNumbers: [],
      }));
      return;
    }
    if (name === "template") {
      setSelectedTemplate(value?.value || null);
      setTestConfig(prev => ({
        ...prev,
        template: value,
        language: null,
        messageText: null,
        format: null,
        type: null,
        codeLength: null,
      }));
      return;
    }
    if (name === "language") {
      setSelectedLanguage(value?.value || null);
      setTestConfig(prev => ({
        ...prev,
        language: value,
        messageText: null,
        format: null,
        type: null,
        codeLength: null,
      }));
      return;
    }
    if (name === "messageText" && value) {
      setTestConfig({
        ...testConfig,
        messageText: value,
        format: value.format,
        type: value.type,
        codeLength: value.codeLength
      })
    } else if (name === "messageText" && !value) {
      setTestConfig({
        ...testConfig,
        messageText: null,
        format: null,
        type: null,
        codeLength: null
      })
    } else if (name === "delayTime") {
      // console.log('[FRONTEND - NewTest.js] Delay time selected:', value?.value || 58);
      // console.log('[FRONTEND - NewTest.js] Current test config delay time:', testConfig.delayTime);
      setTestConfig({
        ...testConfig,
        delayTime: value?.value || 2.5
      })
    } else {
      const updatedConfig = {
        ...testConfig,
        [name]: value
      }
      // Create dynamic test name based on template, customer, and operator
      if (["template", "customer", "operator"].includes(name)) {
        const template = name === "template" ? value?.label : testConfig.template?.label
        const customer = name === "customer" ? value?.label : testConfig.customer?.label
        const operator = name === "operator" ? value?.label : testConfig.operator?.label
        if (template || customer || operator) {
          const parts = [template, customer, operator].filter(Boolean)
          updatedConfig.testName = parts.join(" - ") || "Unnamed Test"
        }
      }
      setTestConfig(updatedConfig)
    }
  }

  const handleInputChange = (e) => {
    // Don't update the form if in view mode
    if (viewMode || formDisabled) return;
    
    const { name, value } = e.target
    // console.log(name, value);
    
    setTestConfig({
      ...testConfig,
      [name]: value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setFormError("")
    try {
      // Validate required fields
      if (!testConfig.testName) {
        setFormError("Test name is required")
        return
      }
      
      if (!testConfig.phoneNumbers || testConfig.phoneNumbers.length === 0) {
        setFormError("At least one phone number is required")
        return
      }

      if (!testConfig.messageText) {
        setFormError("Message text is required")
        return
      }

      if (!testConfig.template || !testConfig.template.label) {
        setFormError("Template with label is required")
        return
      }
      
      // console.log(testConfig);
      
      const result = await runTest({
        name: testConfig.testName,
        customer: testConfig.customer, // Send the complete customer object
        operator: testConfig.operator?.label || "Test Operator",
        phoneNumbers: (testConfig.phoneNumbers || []).map(p => p.value),
        phoneNumberObjects: (testConfig.phoneNumbers || []).map(p => ({
          value: p.value,
          label: p.label
        })),
        template: testConfig.template?.value || "",
        templateLabel: testConfig.template?.label || "Default Template", // Ensure templateLabel is always set
        language: testConfig.language?.label || "",
        messageText: testConfig.messageText?.value || testConfig.messageText || "",
        format: testConfig.format,
        type: testConfig.type,
        codeLength: testConfig.codeLength,
        customSupplierId: testConfig.customSupplierId ? parseInt(testConfig.customSupplierId) : null, // Include custom supplier ID
        routes: (testConfig.routes || []).map(r => ({
          partnerId: r.partnerId || "3", // Default to a partner ID if not available
          routeType: r.routeType || r.value // This should be a string value
        })),
        delayTime: testConfig.delayTime // Include delay time
      }).unwrap();
      
      // Log the response to debug
      // console.log('[FRONTEND - NewTest.js] Test session created:', result);
      // console.log('[FRONTEND - NewTest.js] Delay time sent to backend:', testConfig.delayTime);
      // console.log('[FRONTEND - NewTest.js] Custom supplier ID sent:', testConfig.customSupplierId);
      
      // Check if session created successfully
      if (result?.success && result?.data?.id) {
        // Redirect directly to the test details page
        window.location.href = `/run-test/new?id=${result.data.id}&view=true`;
      } else {
        // Show error message
        setFormError(result?.message || "Unknown error occurred while creating the test session");
      }
    } catch (err) {
      console.error("Error creating test session:", err);
      setFormError(
        err.data?.message || 
        err.error?.message || 
        err.message || 
        "An error occurred while creating the test session"
      );
    }
  }

  // Styles
  const cardStyle = {
    borderRadius: "8px",
    border: "none",
    boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
  }
  
  const sectionStyle = {
    marginBottom: "24px",
  }
  
  const sectionTitleStyle = {
    fontSize: "18px",
    fontWeight: "500",
    marginBottom: "12px",
    color: "#333",
  }
  
  const labelStyle = {
    fontSize: "14px",
    fontWeight: "normal",
    color: "#555",
    marginBottom: "8px",
  }
  
  const inputStyle = {
    fontSize: "14px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    height: "38px",
  }
  
  const infoTextStyle = {
    fontSize: "14px", 
    color: "#666", 
    marginTop: "10px",
    marginBottom: "5px",
  }

  // Update test name when template, customer, or operator changes
  useEffect(() => {
    const template = testConfig.template?.label
    const customer = testConfig.customer?.label
    const operator = testConfig.operator?.label
    
    if (template || customer || operator) {
      const parts = [template, customer, operator].filter(Boolean)
      setTestConfig(prev => ({
        ...prev,
        testName: parts.join(" - ") || "Unnamed Test"
      }))
    }
  }, [testConfig.template, testConfig.customer, testConfig.operator])

  // Refactored socket connection logic
  useEffect(() => {
    const sessionStatus = testData?.data?.status || testData?.status;
    log('SOCKET_EFFECT_START', { viewMode, testId, sessionStatus, isConnected });

    // Clean up any previous socket instance
    if (socket) {
      log('SOCKET_CLEANUP_PREVIOUS', { testId });
      socket.disconnect();
      setSocket(null);
    }

    // Only connect if we have a testId and are in viewMode
    if (viewMode && testId) {
      // If session is completed/aborted/stopped/failed, do not connect
      if (["completed", "aborted", "stopped", "failed"].includes(sessionStatus)) {
        log('SOCKET_NOT_CONNECTING_SESSION_ENDED', { testId, sessionStatus });
        return;
      }
      // Create and set up socket
      log('SOCKET_CREATING_INSTANCE', { testId });
      const socketInstance = io(urls.SOCKET_URL, {
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 20,
        reconnection: true,
        reconnectionDelay: 1000,
        query: {
          sessionId: testId,
          auth: localStorage.getItem("authUser") || ""
        },
        transports: ['websocket', 'polling'],
        timeout: 20000,
        forceNew: true,
        autoConnect: true
      });
      setSocket(socketInstance);
      log('SOCKET_INSTANCE_CREATED', { testId });

      // Always join the session when (re)connecting
      const joinSession = () => {
        if (testId && socketInstance && socketInstance.connected) {
          socketInstance.emit('join-session', testId);
          console.log(`Emitted join-session event for session: ${testId}`);
        }
      };

      socketInstance.on('connect', joinSession);
      socketInstance.on('reconnect', joinSession);

      // If already connected, join immediately
      if (socketInstance.connected) {
        joinSession();
      }

      // Debug function to log all events
      const debugEvent = (eventName) => {
        const originalOn = socketInstance.on.bind(socketInstance);
        socketInstance.on = (event, callback) => {
          if (event === eventName) {
            return originalOn(event, (...args) => {
              console.log(`[Socket Debug] Received ${event} with data:`, args);
              return callback(...args);
            });
          }
          return originalOn(event, callback);
        };
      };
      
      // Debug all relevant events
      debugEvent('session:progress');
      debugEvent('session:result');
      debugEvent('session:completed');
      debugEvent('session:paused');
      debugEvent('session:result-update'); // Debug the new event type
      
      socketInstance.on('connect', () => {
        log('SOCKET_CONNECTED', { testId, socketId: socketInstance.id });
        setIsConnected(true);
        
        // Wait a short time before joining to ensure connection is fully established
        setTimeout(() => {
          socketInstance.emit('join-session', testId);
          log('SOCKET_JOIN_SESSION_EMITTED', { testId });
        }, 200);
      });
      
      socketInstance.on('reconnect', (attemptNumber) => {
        log('SOCKET_RECONNECTED', { testId, attemptNumber });
        // Rejoin the session after reconnection with correct event name
        socketInstance.emit('join-session', testId);
      });
      
      socketInstance.on('reconnect_attempt', (attemptNumber) => {
        log('SOCKET_RECONNECT_ATTEMPT', { testId, attemptNumber });
      });
      
      socketInstance.on('reconnect_error', (error) => {
        log('SOCKET_RECONNECT_ERROR', { testId, error: error.message });
      });
      
      socketInstance.on('disconnect', (reason) => {
        log('SOCKET_DISCONNECTED', { testId, reason });
        setIsConnected(false);
        
        // If the server closed the connection, attempt to reconnect manually
        if (reason === 'io server disconnect') {
          socketInstance.connect();
        }
      });
      
      socketInstance.on('connect_error', (error) => {
        log('SOCKET_CONNECT_ERROR', { testId, error: error.message });
      });
      
      socketInstance.on('session:progress', (data) => {
        console.log('Progress update received:', data);
        setRealTimeProgress(data.progress);
        setRealTimeStatus('running');
        if (data.position && data.total) {
          setProgressDetails({
            position: data.position,
            total: data.total
          });
        }
        if (data.result) {
          const resultData = {
            id: data.result.testId,
            smsc: data.result.msisdn || '+94123123123',
            status: data.result.receiptStatus === 'DELIVERED' ? 'delivered' : 
                   data.result.receiptStatus === 'REJECTED' ? 'failed' : 'pending',
            deliveryStatus: data.result.receiptStatus,
            message: `Test code ${data.result.testCode} (Supplier ID: ${data.result.supplierId})`,
            deliveryTime: new Date().toISOString(),
            supplierId: data.result.supplierId,
            supplierName: data.result.supplierName || `Supplier ${data.result.supplierId}`,
            testCode: data.result.testCode,
            msisdn: data.result.msisdn
          };
          setRealTimeResults(prevResults => {
            const idx = prevResults.findIndex(
              r => r.id === resultData.id || 
                   (r.smsc === resultData.smsc && r.supplierId === resultData.supplierId)
            );
            // Check for pending retry info
            let retryInfo = null;
            setPendingRetries(prevPending => {
              const key = String(resultData.supplierId);
              if (prevPending[key]) {
                retryInfo = prevPending[key];
                // Remove after applying
                const { [key]: _, ...rest } = prevPending;
                return rest;
              }
              return prevPending;
            });
            const mergedResult = retryInfo ? { ...resultData, ...retryInfo } : resultData;
            if (idx === -1) {
              setHasNewMessages(true);
              setTimeout(() => setHasNewMessages(false), 3000);
              return [mergedResult, ...prevResults];
            } else {
              const updated = [...prevResults];
              updated[idx] = { ...updated[idx], ...mergedResult };
              return updated;
            }
          });
        }
      });
      
      socketInstance.on('session:completed', () => {
        log('SOCKET_COMPLETED_RECEIVED', { testId });
        console.log('Session completed event received');
        setRealTimeStatus('completed');
      });
      
      socketInstance.on('session:error', (data) => {
        log('SOCKET_ERROR_RECEIVED', { testId, error: data });
        console.error('Socket session error:', data);
      });
      
      socketInstance.on('session:supplier-error', (data) => {
        log('SOCKET_SUPPLIER_ERROR_RECEIVED', { testId, supplierId: data.supplierId });
        console.error('Supplier error received:', data);
        // You can handle supplier-specific errors here
      });
      
      socketInstance.on('session:paused', () => {
        log('SOCKET_PAUSED_RECEIVED', { testId, currentRealTimeStatus: realTimeStatus });
        console.log('Session paused event received');
        setRealTimeStatus(prevStatus => {
          // Only set to paused if not already paused to prevent duplicate processing
          if (prevStatus !== 'paused') {
            console.log('Setting status to paused from:', prevStatus);
            return 'paused';
          } else {
            console.log('Already paused, ignoring duplicate event');
            return prevStatus;
          }
        });
      });

      // Add handlers for test results
      socketInstance.on('session:result', (data) => {
        console.log('New result received:', data);
        setRealTimeResults(prevResults => {
          // Check if this result already exists by some identifier
          const exists = prevResults.some(
            r => r.id === data.id || 
            (r.smsc === data.smsc && r.timestamp === data.timestamp) ||
            (r.msisdn === data.msisdn && r.requestTime === data.requestTime)
          );
          
          if (!exists) {
            // Show notification for new messages
            setHasNewMessages(true);
            setTimeout(() => setHasNewMessages(false), 3000);
            
            // Add new result to the top of the list
            return [data, ...prevResults];
          }
          return prevResults;
        });
      });
      
      socketInstance.on('session:results', (data) => {
        console.log('Batch results received:', data);
        if (Array.isArray(data)) {
          setRealTimeResults(data);
        } else if (data.results && Array.isArray(data.results)) {
          setRealTimeResults(data.results);
        }
        // Show notification for new batch of results
        setHasNewMessages(true);
        setTimeout(() => setHasNewMessages(false), 3000);
      });
      
      // Handle result updates from callbacks
      socketInstance.on('session:result-update', (data) => {
        console.log('Result update from callback received:', data);
        
        // Update the status of an existing result
        setRealTimeResults(prevResults => {
          return prevResults.map(result => {
            // Match by test ID
            if (result.id === data.testId || 
                (result.supplierId === data.supplierId && result.smsc === data.msisdn)) {
              
              // Update status based on receipt status
              const status = data.receiptStatus === 'POSITIVE' ? 'delivered' :
                            data.receiptStatus === 'NOT_DELIVERED' ? 'failed' :
                            data.dlrStatus === 'DELIVERED' ? 'delivered' :
                            data.dlrStatus === 'REJECTED' || data.dlrStatus === 'UNDELIVERABLE' ? 'failed' :
                            'pending';
              
              // Create updated result with new status information
              return {
                ...result,
                status: status,
                deliveryStatus: data.receiptStatus,
                dlrStatus: data.dlrStatus,
                testCode: data.testCode || result.testCode,
                deliveryTime: data.receiptStatus === 'POSITIVE' ? new Date().toISOString() : result.deliveryTime,
                message: data.testCode ? 
                  `Test code ${data.testCode} (Supplier ID: ${result.supplierId})` :
                  result.message
              };
            }
            return result;
          });
        });
        
        // Show notification for status update
        setHasNewMessages(true);
        setTimeout(() => setHasNewMessages(false), 3000);
      });
      
      // Add session:retry event listener
      socketInstance.on('session:retry', (data) => {
        console.log('Retry event received:', data);
        setRealTimeResults(prevResults => {
          let found = false;
          const updated = prevResults.map(result => {
            if (result.supplierId === data.supplierId) {
              found = true;
              return {
                ...result,
                status: 'retrying',
                retryReason: data.reason,
                lastRetry: new Date().toISOString()
              };
            }
            return result;
          });
          if (!found) {
            // Add a new row to realTimeResults for this retry event
            return [
              {
                status: 'retrying',
                retryReason: data.reason,
                lastRetry: new Date().toISOString(),
                supplierId: data.supplierId,
                supplierName: data.supplierName || `Supplier ${data.supplierId}`,
                smsc: data.msisdn || '',
                message: '',
                deliveryStatus: '',
                deliveryTime: new Date().toISOString(),
                testCode: '',
                testId: data.testId || '',
                id: data.testId || data.supplierId || Math.random().toString(36).substr(2, 9)
              },
              ...updated
            ];
          }
          return updated;
        });
        setHasNewMessages(true);
        setTimeout(() => setHasNewMessages(false), 3000);
      });
      
      // Cleanup function
      return () => {
        if (socketInstance) {
          console.log('[Socket Debug] Cleaning up socketInstance');
          socketInstance.off();
          socketInstance.disconnect();
        }
      };
    }
    // If not connecting, just return a cleanup function for any previous socket
    return () => {
      if (socket) {
        console.log('[Socket Debug] Final cleanup of socket');
        socket.disconnect();
        setSocket(null);
      }
    };
  }, [viewMode, testId, testData]);

  // Update results from API when they change
  useEffect(() => {
    if (resultsData?.results && resultsData.results.length > 0) {
      // If we don't have real-time results yet, use the API results
      if (realTimeResults.length === 0) {
        setRealTimeResults(resultsData.results);
      }
      // If we have fewer real-time results than API results, and not connected, use API results
      else if (realTimeResults.length < resultsData.results.length && !isConnected) {
        setRealTimeResults(resultsData.results);
      }
    }
  }, [resultsData, realTimeResults.length, isConnected]);
  
  // Handle existing test data
  useEffect(() => {
    if (testData && viewMode) {
      // Set form in view-only mode
      setFormDisabled(true);
      setShowResults(true);
      
      // Extract information from test data
      // Check if data is nested inside a 'data' property (API response structure)
      const sessionData = testData.data || testData;
      const configData = sessionData.config || {};
      
      // Set status and progress
      setRealTimeStatus(sessionData.status);
      setRealTimeProgress(sessionData.progress);
      // Clear realTimeResults if completed
      if (sessionData.status === 'completed') {
        setRealTimeResults([]);
      }
      
      // Log the data for debugging
      console.log("Session data:", sessionData);
      console.log("Config data:", configData);
      console.log("Custom Supplier ID from config:", configData.customSupplierId);
      console.log("Routes from config:", configData.routes);
      console.log("Language from config:", configData.language);
      
      // Extract template name from test name 
      const testNameParts = sessionData.name ? sessionData.name.split(' - ') : [];
      const templateName = testNameParts[0] || '';
      const customerName = configData.customer || testNameParts[1] || '';
      const operatorName = testNameParts[2] || '';
      
      // Find matching customer, operator and template in the options
      const customerOption = customerOptions.find(c => c.label === customerName) || 
        { label: customerName || "Unknown Customer", value: customerName || "unknown" };
        
      const operatorOption = operatorOptions.find(o => o.label.includes(operatorName)) || 
        { label: operatorName || "Unknown Operator", value: "unknown" };
        
      const templateOption = templateOptions.find(t => t.label === templateName) || 
        { label: templateName || "Unknown Template", value: "unknown" };
      
      // Set selected IDs to ensure proper loading of dependent dropdowns
      if (customerOption.value !== "unknown") setSelectedCustomer(customerOption.value);
      if (operatorOption.value !== "unknown") setSelectedOperator(operatorOption.value);
      if (templateOption.value !== "unknown") setSelectedTemplate(templateOption.value);
      
      // Set language from config data if available, otherwise default to english
      const languageValue = configData.language ? 
        (typeof configData.language === 'object' ? configData.language.value : configData.language) :
        'english';
      setSelectedLanguage(languageValue);
      
      // Process routes from the config
      let routeOptions = [];
      
      // First check for routes array
      if (configData.routes && Array.isArray(configData.routes) && configData.routes.length > 0) {
        routeOptions = configData.routes.map(route => {
          // Handle routeType being an object with name property
          const routeTypeName = route.routeType && typeof route.routeType === 'object' && route.routeType.name 
            ? route.routeType.name 
            : (typeof route.routeType === 'string' ? route.routeType : "Unknown");
            
          return {
            value: routeTypeName,
            label: routeTypeName,
            routeType: routeTypeName,
            partnerId: route.partnerId || "3" // Ensure partnerId is included
          };
        });
      } 
      // If no routes array, check for routeselection fields
      else {
        if (configData.routeselection1) {
          routeOptions.push({
            value: configData.routeselection1,
            label: configData.routeselection1,
            routeType: configData.routeselection1
          });
        }
        if (configData.routeselection2) {
          routeOptions.push({
            value: configData.routeselection2,
            label: configData.routeselection2,
            routeType: configData.routeselection2
          });
        }
      }
      
      // Set test config with available data
      setTestConfig({
        testName: sessionData.name || "Unnamed Test",
        customer: customerOption,
        operator: operatorOption,
        // phoneNumbers: [
        //   ...(configData.msisdn1 ? [{ label: configData.msisdn1, value: configData.msisdn1 }] : []),
        //   ...(configData.msisdn2 ? [{ label: configData.msisdn2, value: configData.msisdn2 }] : [])
        // ],
        phoneNumbers: (configData.phoneNumberObjects || []).map(p => ({
          value: p.value,
          label: p.label
        })),
        template: templateOption,
        language: configData.language ? 
          (typeof configData.language === 'object' ? configData.language : { label: configData.language, value: configData.language }) :
          { label: 'English', value: 'english' }, // Default if not available
        messageText: { label: configData.text, value: configData.text },
        format: configData.code_format || configData.code || 'Numeric',
        type: configData.code_case || configData.type || 'Mixed',
        codeLength: configData.length || configData.codeLength || '6',
        routes: routeOptions,
        // Include customSupplierId from the config data
        customSupplierId: configData.customSupplierId || null,
        // Include delayTime from saved config (stored as snooze in backend)
        delayTime: configData.snooze || configData.delayTime || 2.5
      });
      
      // console.log('[FRONTEND - NewTest.js] Loading existing test with delay time:', configData.snooze || configData.delayTime || 58);
      // console.log('[FRONTEND - NewTest.js] Config snooze value:', configData.snooze);
      // console.log('[FRONTEND - NewTest.js] Config delayTime value:', configData.delayTime);
    }
  }, [testData, viewMode, customerOptions, operatorOptions, templateOptions]);
  
  // Handle start test
  const handleStartTest = async () => {
    if (!testId) return;
    
    try {
      console.log(`Starting test with ID: ${testId}`);
      const result = await startTest(testId).unwrap();
      console.log('Start test result:', result);
      
      // If we don't have a socket connection yet, establish one
      if (!socket || !isConnected) {
        console.log('No socket connection detected, reconnecting...');
        
        // Disconnect any existing socket
        if (socket) {
          console.log('Disconnecting existing socket');
          socket.disconnect();
        }
        
        const socketInstance = io(urls.SOCKET_URL, {
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 20,
          reconnection: true,
          reconnectionDelay: 1000,
          query: { 
            sessionId: testId,
            auth: localStorage.getItem("authUser") || ""
          },
          transports: ['websocket', 'polling'],
          timeout: 20000,
          forceNew: true,
          autoConnect: true
        });
        
        setSocket(socketInstance);
        
        socketInstance.on('connect', () => {
          console.log('Socket reconnected after starting test');
          setIsConnected(true);
          
          // Wait a short time before joining to ensure connection is fully established
          setTimeout(() => {
            socketInstance.emit('join-session', testId);
            console.log(`Emitted join-session event for session: ${testId}`);
          }, 200);
        });
        
        socketInstance.on('reconnect', (attemptNumber) => {
          console.log(`Reconnected to socket server after ${attemptNumber} attempts`);
          // Rejoin the session after reconnection with correct event name
          socketInstance.emit('join-session', testId);
        });
        
        socketInstance.on('reconnect_attempt', (attemptNumber) => {
          console.log(`Reconnection attempt #${attemptNumber}`);
        });
        
        socketInstance.on('reconnect_error', (error) => {
          console.error('Reconnection error:', error);
        });
        
        socketInstance.on('disconnect', (reason) => {
          console.log(`Disconnected from socket server. Reason: ${reason}`);
          setIsConnected(false);
          
          // If the server closed the connection, attempt to reconnect manually
          if (reason === 'io server disconnect') {
            socketInstance.connect();
          }
        });
        
        socketInstance.on('connect_error', (error) => {
          console.error('Connection error:', error);
        });
        
        socketInstance.on('session:progress', (data) => {
          console.log('Progress update received:', data);
          setRealTimeProgress(data.progress);
          // Only set status to running if not currently paused
          setRealTimeStatus(prevStatus => {
            if (prevStatus === 'paused') {
              console.log('Progress received but session is paused - keeping paused status');
              return 'paused';
            }
            return 'running';
          });
          if (data.position && data.total) {
            setProgressDetails({
              position: data.position,
              total: data.total
            });
          }
          if (data.result) {
            const resultData = {
              id: data.result.testId,
              smsc: data.result.msisdn || '+94123123123',
              status: data.result.receiptStatus === 'DELIVERED' ? 'delivered' : 
                     data.result.receiptStatus === 'REJECTED' ? 'failed' : 'pending',
              deliveryStatus: data.result.receiptStatus,
              message: `Test code ${data.result.testCode} (Supplier ID: ${data.result.supplierId})`,
              deliveryTime: new Date().toISOString(),
              supplierId: data.result.supplierId,
              supplierName: data.result.supplierName || `Supplier ${data.result.supplierId}`,
              testCode: data.result.testCode,
              msisdn: data.result.msisdn
            };
            setRealTimeResults(prevResults => {
              const idx = prevResults.findIndex(
                r => r.id === resultData.id || 
                     (r.smsc === resultData.smsc && r.supplierId === resultData.supplierId)
              );
              // Check for pending retry info
              let retryInfo = null;
              setPendingRetries(prevPending => {
                const key = String(resultData.supplierId);
                if (prevPending[key]) {
                  retryInfo = prevPending[key];
                  // Remove after applying
                  const { [key]: _, ...rest } = prevPending;
                  return rest;
                }
                return prevPending;
              });
              const mergedResult = retryInfo ? { ...resultData, ...retryInfo } : resultData;
              if (idx === -1) {
                setHasNewMessages(true);
                setTimeout(() => setHasNewMessages(false), 3000);
                return [mergedResult, ...prevResults];
              } else {
                const updated = [...prevResults];
                updated[idx] = { ...updated[idx], ...mergedResult };
                return updated;
              }
            });
          }
        });
        
        socketInstance.on('session:completed', () => {
          console.log('Session completed event received');
          setRealTimeStatus('completed');
        });
        
        socketInstance.on('session:result', (data) => {
          console.log('New result received:', data);
          setRealTimeResults(prevResults => {
            const exists = prevResults.some(
              r => r.id === data.id || 
              (r.smsc === data.smsc && r.timestamp === data.timestamp) ||
              (r.msisdn === data.msisdn && r.requestTime === data.requestTime)
            );
            
            if (!exists) {
              // Show notification for new messages
              setHasNewMessages(true);
              setTimeout(() => setHasNewMessages(false), 3000);
              
              // Add new result to the top of the list
              return [data, ...prevResults];
            }
            return prevResults;
          });
        });
      } else if (socket && isConnected) {
        // Ensure we're joined to the session using ONLY the supported format
        console.log('Rejoining session after starting test');
        
        // Try to disconnect and reconnect if we're getting session ID unknown errors
        socket.off('connect_error');
        socket.on('connect_error', (error) => {
          console.error('Connection error while rejoining:', error);
          if (error.message && error.message.includes('Session ID unknown')) {
            console.log('Reconnecting socket due to session ID error');
            socket.disconnect();
            setTimeout(() => socket.connect(), 500);
          }
        });
        
        // Wait a short time before joining to ensure connection is fully established
        setTimeout(() => {
          socket.emit('join-session', testId);
          console.log(`Re-emitted join-session event for session: ${testId}`);
        }, 200);
      }
      
      // Set status to running if not updated by socket yet
      if (realTimeStatus !== 'running') {
        setRealTimeStatus('running');
      }
      
    } catch (error) {
      console.error("Failed to start test:", error);
      alert("Failed to start test: " + (error.data?.message || error.message || "Unknown error"));
    }
  };

  // Handle pause test
  const handlePauseTest = async () => {
    if (!testId) return;
    
    // Show confirmation modal instead of direct API call
    setPauseModal(true);
  };

  // Execute pause test after confirmation
  const executePauseTest = async () => {
    if (!testId) return;
    
    try {
      setModalLoading(true);
      console.log(`Pausing test with ID: ${testId}`);
      const result = await pauseTest(testId).unwrap();
      console.log('Pause test result:', result);
      
      // Update local status to paused immediately for better UX
      setRealTimeStatus('paused');
      setPauseModal(false);
      setModalLoading(false);
      
    } catch (error) {
      console.error("Failed to pause test:", error);
      setPauseModal(false);
      setModalLoading(false);
      alert("Failed to pause test: " + (error.data?.message || error.message || "Unknown error"));
    }
  };
  
  // State for custom confirmation modals
  const [cancelModal, setCancelModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [pauseModal, setPauseModal] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  // Add state for fancy delete success modal
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false)
  const [deleteSuccessMessage, setDeleteSuccessMessage] = useState("")
  
  // Handle cancel test with custom modal
  const handleCancelTest = async () => {
    if (!testId) return;
    
    // Show custom modal instead of browser dialog
    setCancelModal(true);
  };
  
  // Execute cancel test after confirmation
  const executeCancelTest = async () => {
    if (!testId) return;
    
    try {
      setModalLoading(true);
      await cancelTest(testId);
      setCancelModal(false);
      setModalLoading(false);
    } catch (error) {
      console.error("Failed to cancel test:", error);
      setCancelModal(false);
      setModalLoading(false);
    }
  };
  
  // Handle delete test with custom modal
  const handleDeleteTest = async () => {
    if (!testId) return;
    
    // Show custom modal instead of browser dialog
    setDeleteModal(true);
  };
  
  // Execute delete test after confirmation
  const executeDeleteTest = async () => {
    if (!testId) return;
    
    try {
      setModalLoading(true);
      const result = await deleteTest(testId).unwrap();
      setDeleteModal(false);
      setModalLoading(false);
      
      if (result.success) {
        // Set appropriate success message based on test status
        const actionMessage = realTimeStatus === 'created' ? 'aborted' : 'cancelled';
        setDeleteSuccessMessage(result.message || `Test successfully ${actionMessage}`);
        setShowDeleteSuccessModal(true);
        // Navigation will happen after modal is closed
      } else {
        // Show error message if deletion was not successful
        alert(result.message || "Failed to delete test");
      }
    } catch (error) {
      console.error("Failed to delete test:", error);
      setDeleteModal(false);
      setModalLoading(false);
      alert(error.data?.message || "An error occurred while deleting the test");
    }
  };
  
  // Function to get status badge with more detailed status
  const getStatusBadge = () => {
    const sessionData = testData?.data || testData;
    const status = realTimeStatus || (sessionData?.status || 'unknown');
    let color = 'primary';
    let text = status;
    
    switch (status) {
      case 'completed':
      case 'aborted':
        color = 'success';
        text = status === 'aborted' ? 'Aborted' : 'Completed';
        break;
      case 'stopped':
        color = 'danger';
        text = 'Stopped';
        break;
      case 'failed':
        color = 'danger';
        text = 'Failed';
        break;
      case 'created':
        color = 'warning';
        text = 'Created (Ready to Run)';
        break;
      case 'running':
        color = 'info';
        text = `Running (${progress}%)`;
        break;
      case 'paused':
        color = 'secondary';
        text = `Paused (${progress}%)`;
        break;
      default:
        text = `Status: ${status}`;
    }
    
    return (
      <span className={`badge bg-${color} text-white`}>{text}</span>
    );
  };
  
  // Calculate progress value
  const progress = realTimeProgress !== null ? 
    realTimeProgress : 
    (testData?.data?.progress || testData?.progress || 0);

  // Add state for infinite scrolling
  const [visibleResults, setVisibleResults] = useState(10)
  const [loadingMore, setLoadingMore] = useState(false)
  const resultsContainerRef = useRef(null)
  
  // For tracking new messages
  const [hasNewMessages, setHasNewMessages] = useState(false)

  // Add scroll handler for infinite scrolling
  const handleResultsScroll = () => {
    if (!resultsContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = resultsContainerRef.current;
    const scrolledToBottom = scrollHeight - scrollTop - clientHeight < 50;
    
    if (scrolledToBottom && !loadingMore) {
      setLoadingMore(true);
      setTimeout(() => {
        setVisibleResults(prev => prev + 10);
        setLoadingMore(false);
      }, 300);
    }
  };

  // Reset visible results when switching tests
  useEffect(() => {
    setVisibleResults(10);
  }, [testId]);

  // Restore layout and add auto-scroll logic
  useEffect(() => {
    if (!resultsContainerRef.current) return;
    const results = realTimeResults.length > 0 
      ? realTimeResults 
      : (resultsData && resultsData.results ? resultsData.results : []);
    const sortedResults = [...results].sort((a, b) => {
      const timeA = a.deliveryTime || a.requestTime || new Date().toISOString();
      const timeB = b.deliveryTime || b.requestTime || new Date().toISOString();
      return new Date(timeB) - new Date(timeA);
    });
    if (
      resultsContainerRef.current.scrollHeight <= resultsContainerRef.current.clientHeight &&
      visibleResults < sortedResults.length
    ) {
      setTimeout(() => setVisibleResults(v => Math.min(v + 10, sortedResults.length)), 0);
    }
  }, [realTimeResults, resultsData, visibleResults]);

  // Add after hooks, before render logic:
  const isCompleted = ['completed', 'aborted'].includes(realTimeStatus || (testData?.data?.status || testData?.status));
  const results = isCompleted
    ? (resultsData && resultsData.results ? resultsData.results : [])
    : (realTimeResults.length > 0 
        ? realTimeResults 
        : (resultsData && resultsData.results ? resultsData.results : []));

  return (
    <React.Fragment>
      <style>
        {`
          @keyframes highlightNew {
            0% { background-color: rgba(129, 17, 19, 0.1); }
            100% { background-color: #ffffff; }
          }
          
          @keyframes fadeInOut {
            0% { opacity: 0; transform: translateY(-20px); }
            10% { opacity: 1; transform: translateY(0); }
            90% { opacity: 1; transform: translateY(0); }
            100% { opacity: 0; transform: translateY(-20px); }
          }
        `}
      </style>
      <div className="page-content" style={{ background: "#f8f9fa", padding: "100px 0 30px 0" }}>
        <Container fluid>
          <div style={{ 
            fontSize: "12px", 
            color: "#811113", 
            fontWeight: "500", 
            marginBottom: "20px",
            paddingLeft: "15px",
          }}>
            <span>TESTS</span>
            <span style={{ color: "#555", margin: "0 5px" }}>/</span>
            <span style={{ color: "#555" }}>{testConfig.testName}</span>
          </div>
          
          {viewMode && (
            <div className="mb-4 ps-3">
              <div className="d-flex align-items-center mb-3">
                <h5 className="mb-0 me-2" style={{ fontSize: "18px", color: "#333", fontWeight: "normal" }}>Status:</h5>
                {getStatusBadge()}
              </div>
              
              {(realTimeStatus === 'running' || realTimeStatus === 'paused') && (
                <div className="mt-2" style={{ maxWidth: "300px" }}>
                  <div className="mb-1 d-flex justify-content-between">
                    <small style={{ fontSize: "12px", color: "#666" }}>
                      {/* Check for position info in progress updates */}
                      {progressDetails?.position && progressDetails?.total 
                        ? `${progressDetails.position} of ${progressDetails.total} suppliers tested` 
                        : `Progress: ${progress}%`}
                    </small>
                    <small style={{ fontSize: "12px", color: "#666" }}>{progress}%</small>
                  </div>
                  <Progress 
                    value={progress} 
                    className="w-100" 
                    style={{ height: '10px' }}
                  />
                </div>
              )}
            </div>
          )}
          
          <Row className="justify-content-center align-items-stretch">
            <Col xs={12} md={5} style={{ paddingLeft: "25px", paddingRight: "15px", marginBottom: 32, display: "flex", flexDirection: "column" }}>
              <Card style={{ ...cardStyle, height: "100%", display: "flex", flexDirection: "column", flex: 1 }}>
                <CardBody style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
                  {formError && (
                    <Alert color="danger">{formError}</Alert>
                  )}
                  
                  {error && (
                    <Alert color="danger">
                      {error.data?.message || "An error occurred while running the test"}
                    </Alert>
                  )}
                  
                  <Form onSubmit={handleSubmit}>
                    <div style={sectionStyle}>
                      <div style={sectionTitleStyle}>General Settings</div>
                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label for="customer" style={labelStyle}>Customer</Label>
                            {customersError ? (
                              <Alert color="danger" className="p-2 mb-2">
                                Error loading customers
                              </Alert>
                            ) : null}
                            <Select
                              className="basic-single css-b62m3t-container"
                              classNamePrefix="select"
                              name="customer"
                              isClearable
                              isDisabled={viewMode || formDisabled || isLoadingCustomers}
                              value={testConfig.customer}
                              onChange={val => handleSelectChange("customer", val)}
                              options={customerOptions}
                              placeholder={isLoadingCustomers ? "Loading customers..." : "Select Customer"}
                              styles={customSelectStyles}
                              isSearchable
                              isLoading={isLoadingCustomers}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="operator" style={labelStyle}>Operator</Label>
                            {operatorsError ? (
                              <Alert color="danger" className="p-2 mb-2">
                                Error loading operators
                              </Alert>
                            ) : null}
                            <Select
                              className="basic-single css-b62m3t-container"
                              classNamePrefix="select"
                              name="operator"
                              value={testConfig.operator}
                              isClearable
                              isDisabled={viewMode || formDisabled || isLoadingOperators}
                              onChange={val => handleSelectChange("operator", val)}
                              options={operatorOptions}
                              placeholder={isLoadingOperators ? "Loading operators..." : "Select Operator"}
                              styles={customSelectStyles}
                              isSearchable
                              isLoading={isLoadingOperators}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label for="phoneNumbers" style={labelStyle}>Phone Numbers</Label>
                            {phoneNumbersError ? (
                              <Alert color="danger" className="p-2 mb-2">
                                Error loading phone numbers
                              </Alert>
                            ) : null}
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              name="phoneNumbers"
                              isClearable
                              value={testConfig.phoneNumbers}
                              onChange={val => handleSelectChange("phoneNumbers", val || [])}
                              options={phoneNumberOptions}
                              placeholder={
                                !selectedCustomer || !selectedOperator 
                                  ? "Select customer and operator first" 
                                  : isLoadingPhoneNumbers
                                    ? "Loading phone numbers..." 
                                    : phoneNumberOptions.length === 0
                                      ? "No phone numbers available" 
                                      : "Select Phone Numbers"
                              }
                              noOptionsMessage={() => 
                                !selectedCustomer || !selectedOperator
                                  ? "Select customer and operator first"
                                  : phoneNumberOptions.length === 0
                                    ? "No phone numbers available for this customer/operator"
                                    : "No options available"
                              }
                              isDisabled={viewMode || formDisabled || !selectedCustomer || !selectedOperator || isLoadingPhoneNumbers}
                              isLoading={isLoadingPhoneNumbers}
                              styles={customSelectStyles}
                              isMulti
                              isSearchable
                              closeMenuOnSelect={false}
                            />
                            {phoneNumberOptions.length === 0 && selectedCustomer && selectedOperator && !isLoadingPhoneNumbers && (
                              <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                                No phone numbers found for this customer and operator. Please add test node contacts in the customer settings.
                              </div>
                            )}
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                    <div style={sectionStyle}>
                      <div style={sectionTitleStyle}>Message Settings</div>
                      <Row className="mb-3">
                        <Col md={6}>
                          <FormGroup>
                            <Label for="template" style={labelStyle}>Template</Label>
                            {templatesError ? (
                              <Alert color="danger" className="p-2 mb-2">
                                Error loading templates
                              </Alert>
                            ) : null}
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              name="template"
                              value={testConfig.template}
                              isClearable
                              onChange={val => handleSelectChange("template", val)}
                              options={templateOptions}
                              placeholder={isLoadingTemplates ? "Loading templates..." : "Select Template"}
                              styles={customSelectStyles}
                              isSearchable
                              isLoading={isLoadingTemplates}
                              isDisabled={viewMode || formDisabled || isLoadingTemplates}
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label for="language" style={labelStyle}>Language</Label>
                            {languagesError ? (
                              <Alert color="danger" className="p-2 mb-2">
                                Error loading languages
                              </Alert>
                            ) : null}
                            <Select
                              className="basic-single css-b62m3t-container"
                              classNamePrefix="select"
                              name="language"
                              isClearable
                              isDisabled={viewMode || formDisabled || !selectedTemplate || isLoadingLanguages}
                              value={testConfig.language}
                              onChange={val => handleSelectChange("language", val)}
                              options={languageOptions}
                              placeholder={
                                !selectedTemplate 
                                  ? "Select template first" 
                                  : isLoadingLanguages
                                    ? "Loading languages..." 
                                    : languageOptions.length === 0
                                      ? "No languages available" 
                                      : "Select Language"
                              }
                              noOptionsMessage={() => 
                                !selectedTemplate
                                  ? "Select template first"
                                  : languageOptions.length === 0
                                    ? "No languages available for this template"
                                    : "No options available"
                              }
                              styles={customSelectStyles}
                              isSearchable
                              isLoading={isLoadingLanguages}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label for="messageText" style={labelStyle}>Message Text</Label>
                            {messagesError ? (
                              <Alert color="danger" className="p-2 mb-2">
                                Error loading message templates
                              </Alert>
                            ) : null}
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              name="messageText"
                              isClearable
                              isDisabled={viewMode || formDisabled || !selectedTemplate || !selectedLanguage || isLoadingMessages}
                              value={testConfig.messageText}
                              onChange={val => handleSelectChange("messageText", val)}
                              options={messageTextOptions}
                              placeholder={
                                !selectedTemplate
                                  ? "Select template first"
                                  : !selectedLanguage
                                    ? "Select language first"
                                    : isLoadingMessages
                                      ? "Loading message templates..."
                                      : messageTextOptions.length === 0
                                        ? "No message templates available"
                                        : "Select Message Text"
                              }
                              noOptionsMessage={() => 
                                !selectedTemplate
                                  ? "Select template first"
                                  : !selectedLanguage
                                    ? "Select language first"
                                    : messageTextOptions.length === 0
                                      ? "No message templates available for this template and language"
                                      : "No options available"
                              }
                              styles={customSelectStyles}
                              isSearchable
                              isLoading={isLoadingMessages}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <div style={infoTextStyle}>
                            {testConfig.messageText && (
                              <>
                                <span style={{marginRight: "10px"}}>Format: <strong>{testConfig.format}</strong></span>
                                <span style={{marginRight: "10px"}}>Type: <strong>{testConfig.type}</strong></span>
                                <span>Code Length: <strong>{testConfig.codeLength}</strong></span>
                              </>
                            )}
                          </div>
                        </Col>
                      </Row>
                    </div>
                    <div style={sectionStyle}>
                      <div style={sectionTitleStyle}>Route Settings</div>
                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label for="routes" style={labelStyle}>Routes</Label>
                            {partnersError ? (
                              <Alert color="danger" className="p-2 mb-2">
                                Error loading routes
                              </Alert>
                            ) : null}
                            <Select
                              className="basic-single"
                              classNamePrefix="select"
                              name="routes"
                              value={testConfig.routes}
                              onChange={val => handleSelectChange("routes", val || [])}
                              options={routeOptions}
                              placeholder={isLoadingPartners ? "Loading routes..." : "Select Routes"}
                              styles={customSelectStyles}
                              isMulti
                              isSearchable
                              isClearable
                              closeMenuOnSelect={false}
                              isLoading={isLoadingPartners}
                              isDisabled={viewMode || formDisabled || isLoadingPartners}
                              noOptionsMessage={() => 
                                routeOptions.length === 0 
                                  ? "No routes available" 
                                  : "No options found"
                              }
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                    <div style={sectionStyle}>
                      <Row>
                        <Col md={12}>
                          <FormGroup>
                            <Label for="delayTime" style={labelStyle}>Set Delay Time</Label>
                            <p style={infoTextStyle}>
                              Enter delay time between test requests (1-59 seconds)
                            </p>
                            <input
                              type="number"
                              name="delayTime"
                              min="0"
                              max="59"
                              step="0.1"
                              value={testConfig.delayTime === 0 ? '' : testConfig.delayTime}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === '') {
                                  // If field is cleared, set to 0
                                  setTestConfig({...testConfig, delayTime: 0});
                                } else {
                                  const numValue = parseFloat(value);
                                  if (!isNaN(numValue) && numValue >= 0 && numValue <= 59) {
                                    setTestConfig({...testConfig, delayTime: numValue});
                                  }
                                }
                              }}
                              disabled={viewMode || formDisabled}
                              placeholder="Enter delay time (e.g., 2.5, 10.8)"
                              className="form-control"
                              style={{
                                ...inputStyle,
                                borderColor: viewMode || formDisabled ? '#dee2e6' : '#ced4da'
                              }}
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                    {/* Add this new form group for custom supplier ID right after the Routes section */}
                    <div style={sectionStyle}>
                      <h5 style={sectionTitleStyle}>Custom Supplier ID (Optional)</h5>
                      <p style={infoTextStyle}>
                        Enter a specific supplier ID to run tests only for this supplier.
                      </p>
                      <FormGroup>
                        <Label style={labelStyle} for="customSupplierId">
                          Custom Supplier ID
                        </Label>
                        <input
                          type="number"
                          name="customSupplierId"
                          id="customSupplierId"
                          placeholder="e.g. 71062"
                          value={testConfig.customSupplierId || ''}
                          onChange={handleInputChange}
                          disabled={viewMode || formDisabled}
                          className="form-control"
                          style={inputStyle}
                        />
                        <small className="form-text text-muted">
                          Leave blank to test all selected routes.
                        </small>
                      </FormGroup>
                    </div>
                    
                    {/* Action buttons based on test state and view mode */}
                    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
                      {!viewMode ? (
                      <Button 
                        type="submit" 
                        disabled={
                          isLoading ||
                          !testConfig.customer ||
                          !testConfig.operator ||
                          !testConfig.phoneNumbers?.length ||
                          !testConfig.messageText ||
                          !testConfig.routes || testConfig.routes.length === 0
                        }
                        style={{
                          background: "#811113", 
                          color: "white",
                          border: "none",
                          borderRadius: "4px",
                            padding: "8px 20px",
                            marginLeft: "auto" // Push to right
                        }}
                      >
                        {isLoading ? (
                          <>
                              <Spinner size="sm" className="me-2" /> Running Test...
                            </>
                          ) : "Create Test"}
                        </Button>
                      ) : (
                        <div style={{ width: "100%", display: "flex", justifyContent: "space-between" }}>
                          <Button
                            color="secondary"
                            onClick={() => history.push('/run-test')}
                          >
                            Back to Tests
                          </Button>
                          
                          <div>
                            {(realTimeStatus === 'created' || realTimeStatus === 'stopped') && realTimeStatus !== 'paused' && (
                              <Button 
                                className="me-2" 
                                onClick={handleStartTest}
                                disabled={starting}
                                style={{
                                  background: "#1B5E20", 
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "8px 20px"
                                }}
                              >
                                {starting ? 'Starting...' : realTimeStatus === 'stopped' ? 'Resume Test' : 'Start Test'}
                              </Button>
                            )}
                            
                            {realTimeStatus === 'paused' && (
                              <Button 
                                className="me-2" 
                                onClick={handleStartTest}
                                disabled={starting}
                                style={{
                                  background: "#1B5E20", 
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "8px 20px"
                                }}
                              >
                                {starting ? 'Resuming...' : 'Resume'}
                      </Button>
                            )}
                            
                            {realTimeStatus === 'running' && !['aborted', 'paused'].includes(realTimeStatus) && (
                              <Button 
                                color="warning" 
                                className="me-2" 
                                onClick={handlePauseTest}
                                disabled={pausing || modalLoading}
                              >
                                {pausing || modalLoading ? 'Pausing...' : 'Pause'}
                              </Button>
                            )}
                            
                            {(['stopped', 'failed', 'paused'].includes(realTimeStatus) && realTimeStatus !== 'created') && (
                              <Button 
                                onClick={handleDeleteTest}
                                disabled={deleting || modalLoading}
                                style={{
                                  background: "#811113", 
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "8px 20px"
                                }}
                              >
                                {deleting || modalLoading ? 'Processing...' : 'Cancel'}
                              </Button>
                            )}
                            {(realTimeStatus === 'created') && (
                              <Button 
                                onClick={handleDeleteTest}
                                disabled={deleting || modalLoading}
                                style={{
                                  background: "#811113", 
                                  color: "white",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "8px 20px"
                                }}
                              >
                                {deleting || modalLoading ? 'Processing...' : 'Abort'}
                              </Button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </Form>
                </CardBody>
              </Card>
            </Col>
            
            <Col xs={12} md={7} style={{ paddingLeft: "15px", paddingRight: "25px", height: "calc(100vh - 430px)", marginBottom: 32, display: "flex", flexDirection: "column" }}>
              {/* <Card style={{ ...cardStyle, height: "100%", display: "flex", flexDirection: "column", flex: 1 }}> */}
                <CardBody style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", paddingBottom: 0 }}>
                  {!viewMode ? (
                  <Card style={{ ...cardStyle, height: "100%", display: "flex", flexDirection: "column", flex: 1 }}>
                    <CardBody className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: "500px" }}>
                      <div style={{ textAlign: "center", color: "#777" }}>
                        <div style={{ marginBottom: "15px" }}>
                          <img 
                            src={testSvg}
                            alt="Start Test" 
                            width="64" 
                            height="64" 
                          />
                        </div>
                        <p style={{ fontSize: "14px", fontWeight: "normal", color: "#666" }}>Start the test to view the logs in realtime.</p>
                      </div>
                    </CardBody>
                  </Card>
                  ) : (
                    <Card style={{ ...cardStyle, height: "79vh", display: "flex", flexDirection: "column", flex: 1 }}>
                      <CardBody style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", paddingBottom: 0 }}>
                        {resultsLoading && !resultsData && (
                          <div className="text-center">
                            <Spinner size="sm" />
                            <p>Loading results...</p>
                          </div>
                        )}
                        
                        {resultsError && (
                          <Alert color="danger">
                            {resultsError.data?.message || "An error occurred while fetching test results"}
                          </Alert>
                        )}
                        
                        {testData && (
                          <React.Fragment>
                            {/* {process.env.NODE_ENV === 'development' && (
                              <div className="mb-4 p-3 bg-light">
                                <h6 className="mb-2">Debug Information:</h6>
                                <div style={{ fontSize: '12px' }}>
                                  <p className="mb-1"><strong>Test ID:</strong> {testData.data?.id || testData.id}</p>
                                  <p className="mb-1"><strong>Created:</strong> {new Date(testData.data?.createdAt || testData.createdAt).toLocaleString()}</p>
                                  <p className="mb-1"><strong>Updated:</strong> {new Date(testData.data?.updatedAt || testData.updatedAt).toLocaleString()}</p>
                                  <p className="mb-1"><strong>Progress:</strong> {testData.data?.progress || testData.progress || 0}%</p>
                                  <p className="mb-0"><strong>Status:</strong> {testData.data?.status || testData.status}</p>
                                </div>
                              </div>
                            )} */}
                            
                            {['created', 'running', 'paused', 'stopped'].includes(testData.data?.status || testData.status) && (
                              <Alert color="info">
                                {(testData.data?.status || testData.status) === 'created' ? 
                                  "Test is ready to run. Click 'Start Test' to begin." : 
                                  (testData.data?.status || testData.status) === 'running' ? 
                                    `Test is currently running (${progress}%). Results will update automatically.` : 
                                    (testData.data?.status || testData.status) === 'stopped' ?
                                      "Test was stopped. Click 'Resume Test' to continue." :
                                      `Test is paused at ${progress}%. Results shown are partial.`}
                              </Alert>
                            )}
                          </React.Fragment>
                        )}
                        
                        {/* Results display that matches the image format */}
                        <div className="mt-4" style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                          {hasNewMessages && (
                            <div className="new-message-alert" style={{
                              position: "absolute",
                              top: "10px",
                              right: "10px",
                              padding: "8px 12px",
                              background: "#E8F5E9",
                              color: "#4CAF50",
                              borderRadius: "4px",
                              fontSize: "12px",
                              zIndex: 10,
                              animation: "fadeInOut 3s ease-in-out"
                            }}>
                              New results received
                            </div>
                          )}
                          <div 
                            className="table-responsive" 
                            style={{ flex: 1, minHeight: 0, overflowY: "auto", maxHeight: "100%" }}
                            ref={resultsContainerRef}
                            onScroll={handleResultsScroll}
                          >
                            <table className="table" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                              <thead style={{ position: "sticky", top: 0, zIndex: 5, backgroundColor: "#fff" }}>
                                <tr style={{ background: "#fff" }}>
                                  <th style={{ padding: "16px", fontSize: "14px", fontWeight: "500", color: "#333", borderBottom: "1px solid #dee2e6" }}>Phone Number</th>
                                  <th style={{ padding: "16px", fontSize: "14px", fontWeight: "500", color: "#333", borderBottom: "1px solid #dee2e6" }}>Supplier</th>
                                  <th style={{ padding: "16px", fontSize: "14px", fontWeight: "500", color: "#333", borderBottom: "1px solid #dee2e6" }}>Test Code</th>
                                  <th style={{ padding: "16px", fontSize: "14px", fontWeight: "500", color: "#333", borderBottom: "1px solid #dee2e6" }}>Status</th>
                                  <th style={{ padding: "16px", fontSize: "14px", fontWeight: "500", color: "#333", borderBottom: "1px solid #dee2e6" }}>Timestamp</th>
                                  <th style={{ padding: "16px", fontSize: "14px", fontWeight: "500", color: "#333", borderBottom: "1px solid #dee2e6" }}>Supplier ID</th>
                                  <th style={{ padding: "16px", fontSize: "14px", fontWeight: "500", color: "#333", borderBottom: "1px solid #dee2e6" }}>Test ID</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  const sortedResults = [...results].sort((a, b) => {
                                    const timeA = a.deliveryTime || a.requestTime || new Date().toISOString();
                                    const timeB = b.deliveryTime || b.requestTime || new Date().toISOString();
                                    return new Date(timeB) - new Date(timeA);
                                  });
                                  const displayedResults = sortedResults.slice(0, visibleResults);
                                  if (displayedResults.length > 0) {
                                    return displayedResults.map((result, idx) => (
                                      <tr 
                                        key={`${result.id || result.testId || idx}-${idx}`}
                                        style={{ 
                                          background: "#fff",
                                          animation: idx === 0 && hasNewMessages ? "highlightNew 3s ease-out" : "none"
                                        }}
                                      >
                                        <td style={{ padding: "16px", fontSize: "14px", color: "#333", borderBottom: "1px solid #dee2e6" }}>
                                          {result.smsc || result.msisdn || '+94123123123'}
                                        </td>
                                        <td style={{ padding: "16px", fontSize: "14px", color: "#333", borderBottom: "1px solid #dee2e6" }}>
                                          {result.supplierName || `Supplier ${result.supplierId}` || 'Unknown'}
                                        </td>
                                        <td style={{ padding: "16px", fontSize: "14px", color: "#333", borderBottom: "1px solid #dee2e6" }}>
                                          {result.testCode || 'N/A'}
                                        </td>
                                        <td style={{ padding: "16px", fontSize: "14px", color: "#333", borderBottom: "1px solid #dee2e6" }}>
                                          <span style={{
                                            display: "inline-block",
                                            padding: "4px 8px",
                                            borderRadius: "4px",
                                            fontSize: "12px",
                                            fontWeight: "400",
                                            backgroundColor: 
                                              result.status === 'delivered' || result.deliveryStatus === 'DELIVERED' ? '#E8F5E9' : 
                                              result.status === 'failed' || result.deliveryStatus === 'REJECTED' || result.deliveryStatus === 'Errored' ? '#FFEBEE' : 
                                              '#FFF8E1',
                                            color: 
                                              result.status === 'delivered' || result.deliveryStatus === 'DELIVERED' ? '#4CAF50' : 
                                              result.status === 'failed' || result.deliveryStatus === 'REJECTED' || result.deliveryStatus === 'Errored' ? '#F44336' : 
                                              '#FF9800'
                                          }}>
                                            {result.deliveryStatus || (
                                              result.status === 'delivered' ? 'Delivered' : 
                                              result.status === 'failed' ? 'Errored' : 
                                              'Not Delivered'
                                            )}
                                          </span>
                                        </td>
                                        <td style={{ padding: "16px", fontSize: "14px", color: "#333", borderBottom: "1px solid #dee2e6" }}>
                                          {result.deliveryTime ? 
                                            new Date(result.deliveryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) + ' ' + 
                                            new Date(result.deliveryTime).toLocaleDateString([], {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '/') : 
                                            '16:22 13/04/2025'}
                                        </td>
                                        <td style={{ padding: "16px", fontSize: "14px", color: "#333", borderBottom: "1px solid #dee2e6" }}>
                                          {result.supplierId || 'N/A'}
                                        </td>
                                        <td style={{ padding: "16px", fontSize: "14px", color: "#333", borderBottom: "1px solid #dee2e6" }}>
                                          {result.testId || result.id || 'N/A'}
                                        </td>
                                      </tr>
                                    ));
                                  } else {
                                    return (
                                      <tr>
                                        <td colSpan="7" className="text-center" style={{ padding: "20px", borderBottom: "1px solid #dee2e6" }}>
                                          No results available yet
                                        </td>
                                      </tr>
                                    );
                                  }
                                })()}
                              </tbody>
                            </table>
                          </div>
                          
                          {/* Loading spinner outside scrollable area to prevent twitching */}
                          <div style={{ height: "40px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {loadingMore && (
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <Spinner size="sm" color="secondary" />
                                <span className="ms-2" style={{ fontSize: "14px", color: "#666" }}>Loading more results...</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardBody>
                      {/* Fixed Export CSV button pinned to the bottom of the card */}
                      <div className="d-flex justify-content-end mt-3" style={{ padding: "0 1.5rem 1.5rem 1.5rem" }}>
                        <button 
                          type="button"
                          className="custom-export-button"
                          style={{
                            border: "1px solid #E53935",
                            backgroundColor: "#FFFFFF",
                            color: "#E53935",
                            fontSize: "14px",
                            fontWeight: "400",
                            padding: "8px 24px",
                            borderRadius: "4px",
                            boxShadow: "none",
                            cursor: "pointer"
                          }}
                          onClick={() => {
                            // Create CSV content from results
                            const results = realTimeResults.length > 0 
                              ? realTimeResults 
                              : (resultsData && resultsData.results ? resultsData.results : []);
                            if (results.length === 0) return;
                            // Format the data for CSV - matching UI table columns exactly
                            const csvData = [
                              ["Phone Number", "Supplier", "Test Code", "Status", "Timestamp", "Supplier ID", "Test ID"],
                              ...results.map(result => [
                                result.msisdn || result.smsc || '+94123123123123123',
                                result.supplierName || `Supplier ${result.supplierId}`,
                                result.testCode || '',
                                result.deliveryStatus || (
                                  result.status === 'delivered' ? 'Delivered' : 
                                  result.status === 'failed' ? 'Errored' : 
                                  'Not Delivered'
                                ),
                                result.deliveryTime ? 
                                  new Date(result.deliveryTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'}) + ' ' + 
                                  new Date(result.deliveryTime).toLocaleDateString([], {day: '2-digit', month: '2-digit', year: 'numeric'}) : 
                                  '16:22 13/04/2025',
                                result.supplierId || 'N/A',
                                result.testId || result.id || 'N/A'
                              ])
                            ];
                            // Convert to CSV format
                            const csvContent = csvData.map(row => row.join(",")).join("\n");
                            // Create a blob and download
                            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                            const url = URL.createObjectURL(blob);
                            const link = document.createElement('a');
                            link.setAttribute('href', url);
                            link.setAttribute('download', `test_results_${testId || new Date().getTime()}.csv`);
                            link.style.visibility = 'hidden';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                          disabled={!(realTimeResults.length > 0 || (resultsData && resultsData.results && resultsData.results.length > 0))}
                        >
                          Export CSV
                        </button>
                      </div>
                    </Card>
                  )}
                </CardBody>
              {/* </Card> */}
            </Col>
          </Row>
        </Container>
      </div>
      
      {/* Custom confirmation modals */}
      <Modal isOpen={cancelModal} toggle={() => setCancelModal(!cancelModal)} centered className="custom-modal">
        <ModalHeader toggle={() => setCancelModal(!cancelModal)} style={{ borderBottom: "none", paddingBottom: 0 }}>
          <span style={{ color: "#333", fontSize: "18px", fontWeight: "500" }}>Pause Test</span>
        </ModalHeader>
        <ModalBody style={{ padding: "20px" }}>
          <p style={{ fontSize: "14px", color: "#555" }}>
            Are you sure you want to pause this test? You can resume it later.
          </p>
        </ModalBody>
        <ModalFooter style={{ borderTop: "none", paddingTop: 0 }}>
          <Button 
            color="secondary" 
            onClick={() => setCancelModal(false)}
            style={{ fontSize: "14px", fontWeight: "400", borderRadius: "4px" }}
            disabled={modalLoading}
          >
            Cancel
          </Button>
          <Button 
            color="warning" 
            onClick={executeCancelTest}
            style={{ fontSize: "14px", fontWeight: "400", borderRadius: "4px" }}
            disabled={modalLoading}
          >
            {modalLoading ? <><Spinner size="sm" className="me-2" /> Pausing...</> : 'Pause Test'}
          </Button>
        </ModalFooter>
      </Modal>
      
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(!deleteModal)} centered className="custom-modal">
        <ModalHeader toggle={() => setDeleteModal(!deleteModal)} style={{ borderBottom: "none", paddingBottom: 0 }}>
          <span style={{ color: "#333", fontSize: "18px", fontWeight: "500" }}>
            {realTimeStatus === 'created' ? 'Abort Test' : 'Cancel Test'}
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: "20px" }}>
          <div className="mb-3" style={{ color: "#F44336", fontSize: "14px", display: "flex", alignItems: "center" }}>
            <i className="mdi mdi-alert-circle me-2" style={{ fontSize: "20px" }}></i>
            This action cannot be undone.
          </div>
          <p style={{ fontSize: "14px", color: "#555" }}>
            {realTimeStatus === 'created' 
              ? "Are you sure you want to abort this test? The test has not been started yet."
              : "Are you sure you want to cancel this test?"}
          </p>
        </ModalBody>
        <ModalFooter style={{ borderTop: "none", paddingTop: 0 }}>
          <Button 
            color="secondary" 
            onClick={() => setDeleteModal(false)}
            style={{ fontSize: "14px", fontWeight: "400", borderRadius: "4px" }}
            disabled={modalLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={executeDeleteTest}
            style={{ 
              fontSize: "14px", 
              fontWeight: "400", 
              borderRadius: "4px",
              background: "#811113", 
              color: "white",
              border: "none"
            }}
            disabled={modalLoading}
          >
            {modalLoading ? <><Spinner size="sm" className="me-2" /> Aborting...</> : realTimeStatus === 'created' ? 'Abort Test' : 'Cancel Test'}
          </Button>
        </ModalFooter>
      </Modal>
      {/* Fancy delete success modal */}
      <Modal isOpen={showDeleteSuccessModal} toggle={() => {
        setShowDeleteSuccessModal(false);
        history.push('/run-test');
      }} centered>
        <ModalHeader style={{ borderBottom: "none", paddingBottom: 0 }}>
          <span style={{ color: "#388e3c", fontSize: "18px", fontWeight: "500" }}>
            {deleteSuccessMessage.includes('aborted') ? 'Test Aborted' : 'Test Cancelled'}
          </span>
        </ModalHeader>
        <ModalBody style={{ padding: "20px", textAlign: "center" }}>
          <i className="mdi mdi-check-circle" style={{ fontSize: "48px", color: "#4CAF50" }}></i>
          <p style={{ fontSize: "16px", color: "#333", marginTop: "16px" }}>
            {deleteSuccessMessage}
          </p>
        </ModalBody>
        <ModalFooter style={{ borderTop: "none", paddingTop: 0 }}>
          <Button 
            color="success" 
            onClick={() => {
              setShowDeleteSuccessModal(false);
              history.push('/run-test');
            }}
            style={{ fontSize: "14px", fontWeight: "400", borderRadius: "4px" }}
          >
            Back to Tests
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* Pause confirmation modal */}
      <Modal isOpen={pauseModal} toggle={() => setPauseModal(!pauseModal)} centered className="custom-modal">
        <ModalHeader toggle={() => setPauseModal(!pauseModal)} style={{ borderBottom: "none", paddingBottom: 0 }}>
          <span style={{ color: "#333", fontSize: "18px", fontWeight: "500" }}>Pause Test</span>
        </ModalHeader>
        <ModalBody style={{ padding: "20px" }}>
          <p style={{ fontSize: "14px", color: "#555" }}>
            Are you sure you want to pause this test? You can resume it later.
          </p>
        </ModalBody>
        <ModalFooter style={{ borderTop: "none", paddingTop: 0 }}>
          <Button 
            color="secondary" 
            onClick={() => setPauseModal(false)}
            style={{ fontSize: "14px", fontWeight: "400", borderRadius: "4px" }}
            disabled={modalLoading}
          >
            Cancel
          </Button>
          <Button 
            color="warning" 
            onClick={executePauseTest}
            style={{ fontSize: "14px", fontWeight: "400", borderRadius: "4px" }}
            disabled={modalLoading}
          >
            {modalLoading ? <><Spinner size="sm" className="me-2" /> Pausing...</> : 'Pause Test'}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default NewTest 